import React, { useCallback } from 'react';
import { Upload } from 'lucide-react';

interface DropZoneProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

export function DropZone({ onFileSelect, isLoading }: DropZoneProps) {
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xls') || file.name.endsWith('.xlsx') || file.name.endsWith('.csv'))) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
  }, [onFileSelect]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
    >
      <input
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={handleChange}
        className="hidden"
        id="file-upload"
        disabled={isLoading}
      />
      <label htmlFor="file-upload" className="cursor-pointer">
        <div className="flex flex-col items-center gap-4">
          {isLoading ? (
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-400" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-200">
              {isLoading ? 'Processing...' : 'Drop your LTSS report here'}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              Supports Excel (.xls, .xlsx) and CSV files
            </p>
            <p className="text-xs text-slate-500 mt-2">
              🔒 Your file is processed locally and never uploaded to any server
            </p>
          </div>
        </div>
      </label>
    </div>
  );
}
